import React, { useState } from 'react';
import { ScrollView, Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';
import { useTheme } from '~/components/providers/ThemeProvider';
import { patterns } from '@/constants/patterns';
import type { PatternName } from '@/constants/patterns';
import { APP_VERSION, BUILD_NUMBER } from '@/constants/version';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { Button, ButtonText } from '@/components/gluestack/button';
import ThemeSwitcher from '@/components/ui/ThemeSwitcher';
import LanguageSelector from '@/components/ui/LanguageSelector';
import { MenuSelectButton } from '@/components/ui/MenuSelectButton';
import { HStack } from '~/components/gluestack/hstack';
import { VStack } from '~/components/gluestack/vstack';
import { Card } from '~/components/gluestack/card';
import { Heading } from '~/components/gluestack/heading';
import { Divider } from '@/components/gluestack/divider';
import { router, useLocalSearchParams } from 'expo-router';
import { ContextSheet } from '@/components/ui/ContextSheet';
import LocationPermissionHelp from '@/components/ui/LocationPermissionHelp';
import { MemberListSheet } from '@/components/domain/common/MemberListSheet';
import { SettingsItem } from '@/components/ui/SettingsItem';
import { SettingsToggle } from '@/components/ui/SettingsToggle';
import UsernameChangeSheet from '@/components/domain/auth/UsernameChangeSheet';
import EmailChangeSheet from '@/components/domain/auth/EmailChangeSheet';
import PasswordChangeSheet from '@/components/domain/auth/PasswordChangeSheet';
import DeleteAccountSheet from '@/components/domain/auth/DeleteAccountSheet';
import { WhatsNewSheetContent } from '@/components/domain/whats-new/WhatsNewSheetContent';
import { useAuth } from '@/components/providers/AuthProvider';
import { useSignalR } from '@/components/providers/SignalRProvider';
import { triggerGlobalCrash } from '@/components/providers/AppErrorBoundary';
import { ApiService } from '@/services/ApiService';
import { devTestSettings } from '@/services/devTestSettings';
import { useIsStagingApi } from '@/hooks/useApiHostPrefix';
import { useLocation } from '@/components/providers/LocationProvider';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { MembersService } from '@/services/domain/MembersService';
import type { AccountSettingsResponse } from '@/services/domain/AccountTypes';
import { useAccountSettings, useUpdateAccountSetting } from '@/hooks/useAccountSettings';
import { MemberAccount } from '@/models/MemberAccount';

import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  Activity03Icon,
  BirthdayCakeIcon,
  Bug01Icon,
  Delete02Icon,
  EyeIcon,
  GlobeIcon,
  GridViewIcon,
  HotAirBalloonIcon,
  Image02Icon,
  Key01Icon,
  Location03Icon,
  LocationUpdate01Icon,
  Mail02Icon,
  MailAtSign01Icon,
  News01Icon,
  NotificationSquareIcon,
  PaintBrush04Icon,
  Recycle03Icon,
  RulerIcon,
  UserBlock01Icon,
  UserCircleIcon,
  UserUnlock01Icon,
  AppStoreIcon,
  ViewOffIcon,
  WeightScale01Icon,
  Gif01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { DarkModeIcon } from '@hugeicons-pro/core-solid-rounded';
import { useMeasurementPreferences, type DistanceUnit, type WeightUnit, type HeightUnit } from '@/components/providers/MeasurementsProvider';
import { SUPPORT_URLS } from '@/constants/urls';
import type { HugeiconsProps } from '@hugeicons/react-native';
import LocationPlacePicker from '@/components/ui/LocationPlacePicker';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { useImageCacheStats } from '@/hooks/useImageCacheStats';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { communities, type CommunityCode } from '@/constants/communities';
import * as AppIcon from 'expo-quick-actions/icon';

const APP_ICON_SOURCES: Record<string, any> = Object.fromEntries(
  (Object.keys(communities) as CommunityCode[]).map((code) => [code, communities[code].appIcon])
);
const DEFAULT_APP_ICON = require('@/assets/icon.png');

const formatCacheSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${Math.round(mb)} MB`;
};

type SectionHeaderProps = {
  icon?: HugeiconsProps['icon'];
  title: string;
};

const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
  <Heading size="md" className='mt-4 ml-4 text-typography-700'>{title}</Heading>
);

function AppIconSettingsRow() {
  const { t } = useTranslation();
  const [iconSource, setIconSource] = useState<any>(DEFAULT_APP_ICON);

  React.useEffect(() => {
    if (AppIcon.isSupported) {
      AppIcon.getIcon?.().then((name) => {
        setIconSource(APP_ICON_SOURCES[name ?? ''] ?? DEFAULT_APP_ICON);
      });
    }
  }, []);

  return (
    <SettingsItem
      icon={AppStoreIcon}
      label={t('settings.appIcon.title')}
      onPress={() => router.push('/account/settings/app-icon')}
      badge={
        <Image
          source={iconSource}
          style={{ width: 24, height: 24, borderRadius: 6 }}
        />
      }
    />
  );
}

function CrashTestItem({ label, errorMessage }: { label: string; errorMessage: string }) {
  const [shouldCrash, setShouldCrash] = useState(false);
  if (shouldCrash) throw new Error(errorMessage);
  return (
    <SettingsItem
      icon={Bug01Icon}
      label={label}
      rightIcon={null}
      onPress={() => setShouldCrash(true)}
    />
  );
}

function ApiEnvironmentToggle() {
  const { currentCommunity } = useAuth();
  const { connect, disconnect } = useSignalR();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const [useStaging, setUseStaging] = useState(() => ApiService.getApiHostPrefix() === 'staging');
  const [isSwitching, setIsSwitching] = useState(false);

  const apiBaseUrl =
    currentCommunity != null
      ? `${ApiService.getCommunityOrigin(currentCommunity)}/api/v6`
      : undefined;

  const handleChange = async (value: boolean) => {
    setIsSwitching(true);
    try {
      const prefix = value ? 'staging' : 'www';
      await ApiService.persistHostPrefix(prefix);
      setUseStaging(value);
      queryClient.clear();
      await disconnect();
      await connect();
      showSuccessToast(value ? 'Using staging API' : 'Using production API');
    } catch (error) {
      setUseStaging(ApiService.getApiHostPrefix() === 'staging');
      showErrorToast('Failed to switch API environment', error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <SettingsToggle
      icon={GlobeIcon}
      label="Staging API"
      description={apiBaseUrl}
      value={useStaging}
      onValueChange={handleChange}
      disabled={isSwitching}
    />
  );
}

export default function Settings() {
  const { currentPattern, setPatternName } = useTheme();
  const { t } = useTranslation();
  const { accountStatus } = useAuth();
  const isTestUser = accountStatus?.UserName?.toLowerCase().startsWith('testuser') ?? false;
  const isStagingApi = useIsStagingApi();
  const showDeveloperTools = isTestUser || isStagingApi;
  const [forceBillingFailure, setForceBillingFailure] = useState(devTestSettings.forceBillingFailure);
  const openWebPage = useOpenWebPage();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const insets = useSafeAreaInsets();
  const bottomScrollInset = isLargeScreen ? insets.bottom : 0;
  const isLargeScreenWeb = Platform.OS === 'web' && isLargeScreen;

  const {
    weightUnit,
    setWeightUnit: setWeightUnitPref,
    distanceUnit,
    setDistanceUnit,
    heightUnit,
    setHeightUnit,
  } = useMeasurementPreferences();

  const [isBlockedMembersOpen, setIsBlockedMembersOpen] = useState(false);
  const [isUnlockAuthorizedOpen, setIsUnlockAuthorizedOpen] = useState(false);
  const [isUsernameOpen, setIsUsernameOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false);
  const [isLocationHelpOpen, setIsLocationHelpOpen] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const { sizeBytes: cacheSizeBytes, clearNow: clearImageCache } = useImageCacheStats();
  const { panel } = useLocalSearchParams<{ panel?: string | string[] }>();

  // One-shot deep link (e.g. the new-password email): open the sheet, then drop the
  // param so back/refresh doesn't reopen it.
  React.useEffect(() => {
    if (panel !== 'password') return;
    setIsPasswordOpen(true);
    router.setParams({ panel: undefined });
  }, [panel]);

  const { data: accountSettings, isPending: isSettingsLoading } = useAccountSettings();
  const { updateSetting } = useUpdateAccountSetting();

  const themeLabels = {
    auto: t('settings.theme.auto'),
    light: t('settings.theme.light'),
    dark: t('settings.theme.dark'),
  };

  const availablePatterns = Object.keys(patterns) as PatternName[];

  // Location settings state
  const {
    placeMode,
    setPlaceMode,
    setFixedPlace,
    lastAccuracy,
    hasGoodAccuracy,
    hasLocationPermission,
    requestLocationPermission,
    canAskLocationPermissionAgain,
    openLocationSettings,
  } = useLocation();
  const [memberAccount, setMemberAccount] = useState<MemberAccount | null>(null);
  const [isMemberAccountLoading, setIsMemberAccountLoading] = useState<boolean>(true);
  const placeNameUnsubscribeRef = React.useRef<null | (() => void)>(null);

  // placeMode and lastAccuracy are provided by LocationProvider

  React.useEffect(() => {
    const fetchAccount = async () => {
      setIsMemberAccountLoading(true);
      try {
        if (accountStatus?.MemberId) {
          const acc = await MembersService.getMemberAccount(accountStatus.MemberId);
          setMemberAccount(acc);
        } else {
          setMemberAccount(null);
        }
      } catch {
        setMemberAccount(null);
      } finally {
        setIsMemberAccountLoading(false);
      }
    };
    fetchAccount();
  }, [accountStatus?.MemberId]);

  // Subscribe to place name updates while settings is open
  const { subscribePlaceNameUpdates } = useLocation();
  React.useEffect(() => {
    placeNameUnsubscribeRef.current?.();
    const unsub = subscribePlaceNameUpdates((newPlace) => {
      setMemberAccount((prev) => (prev ? { ...prev, PlaceName: newPlace } : prev));
    });
    placeNameUnsubscribeRef.current = unsub;
    return () => unsub();
  }, [subscribePlaceNameUpdates]);

  const handleSettingChange = (
    key: keyof AccountSettingsResponse,
    newValue: boolean,
    onSuccess?: () => void
  ) => {
    if (!accountSettings) return;
    updateSetting(key, newValue, { onSuccess });
  };

  const handleClearCache = async () => {
    if (isClearingCache) return;

    setIsClearingCache(true);
    try {
      queryClient.clear();
      await clearImageCache();
      showSuccessToast(t('settings.diagnostics.cacheCleared'));
    } catch (error) {
      showErrorToast(t('common.error.general'), error);
    } finally {
      setIsClearingCache(false);
    }
  };

  return (
    <>
      <ScreenHeader options={{ title: t('settings.title') }} />
      <ScrollView contentContainerStyle={{ paddingBottom: bottomScrollInset }}>
        <VStack className="mx-auto w-full max-w-4xl p-3 md:pt-0" space="md">
          <SectionHeader title={t('settings.appearance')} />

          <Card className="gap-3" size="md">
            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={DarkModeIcon} size={22} className="color-typography-500" />
                <Text>{t('settings.theme.title')}</Text>
              </HStack>
              <ThemeSwitcher size="sm" labelMap={themeLabels as any} />
            </HStack>

            <Divider />

            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={PaintBrush04Icon} size={22} className="color-typography-500" />
                <Text>{t('settings.pattern')}</Text>
              </HStack>
              {(() => {
                const selectedPatternName =
                  (Object.keys(patterns).find(
                    (key) => patterns[key as PatternName] === currentPattern
                  ) as PatternName | undefined) ?? availablePatterns[0];

                return (
                  <MenuSelectButton
                    selectedKey={selectedPatternName}
                    valueLabel={t(`settings.patterns.${selectedPatternName}`)}
                    options={availablePatterns.map((patternName) => ({
                      key: patternName,
                      label: t(`settings.patterns.${patternName}`),
                    }))}
                    onSelect={(key) => setPatternName(key as PatternName)}
                    placement="bottom right"
                    offset={8}
                    size="sm"
                    className="gap-2"
                    iconClassName="color-white"
                  />
                );
              })()}
            </HStack>

            <Divider />

            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={GlobeIcon} size={22} className="color-typography-500" />
                <Text>{t('settings.language')}</Text>
              </HStack>
              <LanguageSelector size="sm" hideDefaultIcon />
            </HStack>
          </Card>

          <SectionHeader title={t('settings.units.title', 'Units')} />
          <Card className="gap-3" size="md">
            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={Location03Icon} size={22} className="color-typography-500" />
                <Text>{t('settings.units.distance', 'Distance')}</Text>
              </HStack>
              <MenuSelectButton
                selectedKey={distanceUnit}
                valueLabel={
                  distanceUnit === 'km'
                    ? t('settings.units.distanceKm', 'Kilometers (km)')
                    : t('settings.units.distanceMi', 'Miles (mi)')
                }
                options={[
                  { key: 'km', label: t('settings.units.distanceKm', 'Kilometers (km)') },
                  { key: 'mi', label: t('settings.units.distanceMi', 'Miles (mi)') },
                ]}
                onSelect={(key) => setDistanceUnit(key as DistanceUnit)}
                placement="bottom right"
                offset={8}
                size="sm"
                className="gap-2"
                iconClassName="color-white"
              />
            </HStack>

            <Divider />

            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={RulerIcon} size={22} className="color-typography-500" />
                <Text>{t('settings.units.height', 'Height')}</Text>
              </HStack>
              <MenuSelectButton
                selectedKey={heightUnit}
                valueLabel={
                  heightUnit === 'm'
                    ? t('settings.units.heightM', 'Meters (m)')
                    : t('settings.units.heightFt', 'Feet (ft)')
                }
                options={[
                  { key: 'm', label: t('settings.units.heightM', 'Meters (m)') },
                  { key: 'ft', label: t('settings.units.heightFt', 'Feet (ft)') },
                ]}
                onSelect={(key) => setHeightUnit(key as HeightUnit)}
                placement="bottom right"
                offset={8}
                size="sm"
                className="gap-2"
                iconClassName="color-white"
              />
            </HStack>

            <Divider />

            <HStack className="items-center justify-between">
              <HStack className="items-center gap-2">
                <HugeIconsWrapper icon={WeightScale01Icon} size={22} className="color-typography-500" />
                <Text>{t('settings.units.weightUnit', 'Weight')}</Text>
              </HStack>
              <MenuSelectButton
                selectedKey={weightUnit}
                valueLabel={
                  weightUnit === 'kg'
                    ? t('profile.units.weight.kilograms', 'Kilograms (kg)')
                    : weightUnit === 'st'
                      ? t('profile.units.weight.stones', 'Stones (st)')
                      : t('profile.units.weight.pounds', 'Pounds (lb)')
                }
                options={[
                  { key: 'lb', label: t('profile.units.weight.pounds', 'Pounds (lb)') },
                  { key: 'kg', label: t('profile.units.weight.kilograms', 'Kilograms (kg)') },
                  { key: 'st', label: t('profile.units.weight.stones', 'Stones (st)') },
                ]}
                onSelect={(key) => setWeightUnitPref(key as WeightUnit)}
                placement="bottom right"
                offset={8}
                size="sm"
                className="gap-2"
                iconClassName="color-white"
              />
            </HStack>
          </Card>

          <SectionHeader title={t('settings.location.title', 'Location')} />
          <Card className="gap-3" size="md">

            <SettingsItem
              icon={Location03Icon}
              label={t('settings.location.current', 'Published as')}
              badge={
                isMemberAccountLoading ? 'Loading...' : (memberAccount?.PlaceName ?? 'Not set')
              }
              onPress={() => {
                setIsLocationPickerOpen(true);
              }}
            />

            {!hasLocationPermission ? (
              <>
                <Divider />
                <HStack className="items-center justify-between">
                  <HStack className="items-center gap-2">
                    <HugeIconsWrapper
                      icon={LocationUpdate01Icon}
                      size={22}
                      className="color-typography-500"
                    />
                    <Text>{t('settings.location.mode', 'Place name')}</Text>
                  </HStack>
                  <Button
                    size="sm"
                    className="gap-2"
                    onPress={async () => {
                      if (canAskLocationPermissionAgain) {
                        const granted = await requestLocationPermission();
                        if (!granted) {
                          if (Platform.OS === 'web') {
                            setIsLocationHelpOpen(true);
                          } else {
                            // On native, do not show the web help sheet
                            await openLocationSettings();
                          }
                        }
                      } else {
                        // On web, guide with help modal; on native, open settings
                        if (Platform.OS === 'web') {
                          setIsLocationHelpOpen(true);
                        } else {
                          await openLocationSettings();
                        }
                      }
                    }}>
                    <ButtonText>
                      {t('settings.location.enable', 'Enable location...')}
                    </ButtonText>
                  </Button>
                </HStack>
              </>
            ) : (
              hasGoodAccuracy && (
                <>
                  <Divider />
                  <HStack className="items-center justify-between">
                    <HStack className="items-center gap-2">
                      <HugeIconsWrapper
                        icon={LocationUpdate01Icon}
                        size={22}
                        className="color-typography-500"
                      />
                      <Text>{t('settings.location.mode', 'Place name')}</Text>
                    </HStack>
                    <MenuSelectButton
                      selectedKey={placeMode ?? 'auto'}
                      valueLabel={
                        placeMode === 'fixed'
                          ? t('settings.location.fixed', 'Fixed')
                          : t('settings.location.automatic', 'Dynamic')
                      }
                      options={(['auto', 'fixed'] as const).map((mode) => ({
                        key: mode,
                        label:
                          mode === 'fixed'
                            ? t('settings.location.fixed', 'Fixed')
                            : t('settings.location.automatic', 'Dynamic'),
                      }))}
                      onSelect={(key) => setPlaceMode(key as any)}
                      placement="bottom right"
                      offset={8}
                      size="sm"
                      className="gap-2"
                      iconClassName="color-white"
                    />
                  </HStack>
                  {placeMode === 'auto' ? (
                    <Text size="sm" className="-mt-1 ml-8 text-typography-600">
                      {t(
                        'settings.location.autoDescription',
                        'The place name (city) that appears on your profile will be updated as you travel'
                      )}
                    </Text>
                  ) : null}
                </>
              )
            )}

            <Divider />
            <SettingsItem
              label={t('settings.location.tips', 'Tips and Privacy')}
              onPress={() =>
                openWebPage(SUPPORT_URLS.atlassian.articles.unlockPrivatePhotos)
              }
            />
          </Card>

          <SectionHeader title={t('settings.account.title')} />
          <Card className="gap-3" size="md">
            <SettingsItem
              icon={UserCircleIcon}
              label={t('settings.account.username.title_short')}
              badge={accountStatus?.UserName ?? ''}
              onPress={() => setIsUsernameOpen(true)}
            />
            <Divider className="-my-2" />
            <SettingsItem
              icon={MailAtSign01Icon}
              label={t('settings.account.email.title_short')}
              badge={accountStatus?.Email ?? ''}
              onPress={() => setIsEmailOpen(true)}
            />
            <Divider className="-my-2" />
            <SettingsItem
              icon={Key01Icon}
              label={t('settings.account.password.change')}
              onPress={() => setIsPasswordOpen(true)}
            />
          </Card>

          <SectionHeader title={t('settings.preferences.title')} />
          <Card className="gap-3" size="md">
            {isSettingsLoading ? (
              <Text className="text-typography-500">{t('common.loading')}</Text>
            ) : accountSettings ? (
              <>
                <SettingsToggle
                  icon={Mail02Icon}
                  label={t('settings.preferences.emailNotifications')}
                  description={t('settings.preferences.emailNotificationsDescription')}
                  value={accountSettings.EmailNotifications}
                  onValueChange={(value) => handleSettingChange('EmailNotifications', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={BirthdayCakeIcon}
                  label={t('settings.preferences.birthdayList')}
                  description={t('settings.preferences.birthdayListDescription')}
                  value={accountSettings.BirthdayList}
                  onValueChange={(value) => handleSettingChange('BirthdayList', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={News01Icon}
                  label={t('settings.preferences.photosInNewsfeed')}
                  description={t('settings.preferences.photosInNewsfeedDescription')}
                  value={accountSettings.PhotosInNewsfeed}
                  onValueChange={(value) => handleSettingChange('PhotosInNewsfeed', value)}
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={Gif01Icon}
                  label={t('settings.preferences.mediaInComments')}
                  description={t('settings.preferences.mediaInCommentsDescription')}
                  value={accountSettings.MediaInComments}
                  onValueChange={(value) =>
                    handleSettingChange('MediaInComments', value, () => {
                      void queryClient.invalidateQueries({ queryKey: ['comments'] });
                      void queryClient.invalidateQueries({ queryKey: ['posts'] });
                    })
                  }
                />
                <Divider className="-my-2" />
                <SettingsItem
                  icon={NotificationSquareIcon}
                  label={t(isLargeScreenWeb ? 'settings.notifications.titleWithSounds' : Platform.OS === 'web' ? 'settings.notifications.title' : 'notifications.titleNative')}
                  onPress={() => router.push('/account/settings/notifications')}
                />
              </>
            ) : null}
          </Card>

          <SectionHeader title={t('settings.privacy.title')} />
          <Card className="gap-3" size="md">
            {isSettingsLoading ? (
              <Text className="text-typography-500">{t('common.loading')}</Text>
            ) : accountSettings ? (
              <>
                <SettingsItem
                  icon={UserBlock01Icon}
                  label={t('settings.privacy.blockedMembers')}
                  onPress={() => setIsBlockedMembersOpen(true)}
                />
                <Divider className="-my-2" />
                <SettingsItem
                  icon={UserUnlock01Icon}
                  label={t('settings.privacy.photoUnlockAuthorized')}
                  onPress={() => setIsUnlockAuthorizedOpen(true)}
                />
                {Platform.OS !== 'ios' ? (
                  <>
                    <Divider className="-my-2" />
                    <SettingsToggle
                      icon={EyeIcon}
                      label={t('settings.preferences.safeModeOff')}
                      description={t('settings.preferences.safeModeOffDescription')}
                      value={accountSettings.SafeModeOff}
                      onValueChange={(value) => handleSettingChange('SafeModeOff', value)}
                    />
                  </>
                ) : null}
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={ViewOffIcon}
                  label={t('settings.preferences.anonymousViewing')}
                  description={t('settings.preferences.anonymousViewingDescription')}
                  value={accountSettings.AnonymousViewing}
                  onValueChange={(value) => handleSettingChange('AnonymousViewing', value)}
                  badge="Premium"
                />
                <Divider className="-my-2" />
                <SettingsToggle
                  icon={Image02Icon}
                  label={t('settings.preferences.inboundMessagePhotos')}
                  description={t('settings.preferences.inboundMessagePhotosDescription')}
                  value={accountSettings.InboundMessagePhotos}
                  onValueChange={(value) => handleSettingChange('InboundMessagePhotos', value)}
                />
              </>
            ) : null}
          </Card>

          <SectionHeader title={t('settings.about.title')} />
          <Card className="gap-3" size="md">
            <Text>
              {t('settings.about.version')} <Text className="font-bold">{APP_VERSION}</Text>
              {BUILD_NUMBER ? (
                <Text>
                  {' '}
                  ({t('settings.about.build')} {BUILD_NUMBER})
                </Text>
              ) : null}
            </Text>
            <Text size="sm" className="text-typography-500">
              {t('settings.about.madeWithCities')}
            </Text>
            <Divider />
            <SettingsItem
              icon={HotAirBalloonIcon}
              label={t('settings.about.whatsNew')}
              onPress={() => setIsWhatsNewOpen(true)}
            />
            {Platform.OS === 'ios' ? (
              <>
                <Divider />
                <AppIconSettingsRow />
              </>
            ) : null}
            <Divider />
            <SettingsItem
              icon={Recycle03Icon}
              label={t('settings.diagnostics.clearCache')}
              rightIcon={null}
              disabled={isClearingCache}
              onPress={handleClearCache}
              badge={cacheSizeBytes !== null ? formatCacheSize(cacheSizeBytes) : undefined}
            />
          </Card>

          {showDeveloperTools ? (
            <>
              <SectionHeader title="Developer Tools" />
              <Card className="gap-3" size="md">
                <ApiEnvironmentToggle />
                {isTestUser ? (
                  <>
                    <Divider />
                    <SettingsItem
                      icon={Activity03Icon}
                      label={t('settings.diagnostics.signalr')}
                      onPress={() => router.push('/account/settings/signalr')}
                    />
                    <Divider />
                    <SettingsItem
                      icon={GridViewIcon}
                      label="Skeleton Preview"
                      onPress={() => router.push('/account/settings/skeletons')}
                    />
                    <Divider />
                    <SettingsItem
                      icon={Bug01Icon}
                      label="Test Sentry Error"
                      rightIcon={null}
                      onPress={() => {
                        Sentry.captureException(new Error('Test error from Settings'));
                        showSuccessToast('Test error sent to Sentry');
                      }}
                    />
                    <Divider />
                    <CrashTestItem
                      label="Test Route Error Boundary"
                      errorMessage="Test route-level crash from Settings"
                    />
                    <Divider />
                    <SettingsItem
                      icon={Bug01Icon}
                      label="Test Global Error Boundary"
                      rightIcon={null}
                      onPress={() => triggerGlobalCrash('Test global crash from Settings')}
                    />
                    {Platform.OS !== 'web' ? (
                      <>
                        <Divider />
                        <SettingsToggle
                          icon={Bug01Icon}
                          label="Force Billing Failure"
                          description="Reject purchase validation after payment, to test IAP reconciliation. Resets on app restart."
                          value={forceBillingFailure}
                          onValueChange={(next) => {
                            devTestSettings.forceBillingFailure = next;
                            setForceBillingFailure(next);
                          }}
                          badge={forceBillingFailure ? 'ON' : undefined}
                        />
                      </>
                    ) : null}
                  </>
                ) : null}
              </Card>
            </>
          ) : null}

          <SectionHeader title={t('settings.dangerZone')} />
          <Card className="gap-3" size="md">
            <SettingsItem
              icon={Delete02Icon}
              iconClassName="color-error-600"
              labelClassName="text-error-600"
              label={t('settings.account.delete.title')}
              onPress={() => setIsDeleteOpen(true)}
            />
          </Card>
        </VStack>
      </ScrollView>

      <ContextSheet
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        contentComponent={LocationPlacePicker}
        title={t('settings.location.pickPlace', 'Choose a place')}
        payload={{
          onSelect: async (place: any) => {
            try {
              await setFixedPlace(place);
              showSuccessToast(t('settings.location.fixedSet', 'Published location updated'));
            } catch (e) {
              showErrorToast(t('settings.location.fixedError', 'Unable to set location'), e);
            }
          },
        }}
      />

      <ContextSheet
        isOpen={isBlockedMembersOpen}
        onClose={() => setIsBlockedMembersOpen(false)}
        contentComponent={MemberListSheet}
        title={t('settings.privacy.blockedMembers')}
        height="full"
        payload={{ listType: 'blocked', actionMode: 'unblock' }}
      />

      <ContextSheet
        isOpen={isUnlockAuthorizedOpen}
        onClose={() => setIsUnlockAuthorizedOpen(false)}
        contentComponent={MemberListSheet}
        title={t('settings.privacy.photoUnlockAuthorized')}
        height="full"
        payload={{ listType: 'photo-unlock-authorized', actionMode: 'remove-unlock' }}
      />

      {Platform.OS === 'web' ? (
        <ContextSheet
          isOpen={isLocationHelpOpen}
          onClose={() => setIsLocationHelpOpen(false)}
          contentComponent={LocationPermissionHelp}
          title={t('settings.location.enableHelp', 'Enable Location')}
        />
      ) : null}

      <ContextSheet
        isOpen={isUsernameOpen}
        onClose={() => setIsUsernameOpen(false)}
        contentComponent={UsernameChangeSheet}
        title={t('settings.account.username.title')}
      />

      <ContextSheet
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        contentComponent={EmailChangeSheet}
        title={t('settings.account.email.title')}
      />

      <ContextSheet
        isOpen={isPasswordOpen}
        onClose={() => setIsPasswordOpen(false)}
        contentComponent={PasswordChangeSheet}
        title={t('settings.account.password.title')}
      />

      <ContextSheet
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        contentComponent={DeleteAccountSheet}
        title={t('settings.account.delete.title')}
      />

      <ContextSheet
        isOpen={isWhatsNewOpen}
        onClose={() => setIsWhatsNewOpen(false)}
        contentComponent={WhatsNewSheetContent}
        title={t('whatsNew.title')}
      />
    </>
  );
}
